import { getProgressItemKeyFromTrack } from "."
import { Track } from "../av/types"
import { BaseProgressItem } from "./base"

export type ProgressPlayable = {
  position: number
  index: number
  finished: boolean
}

export type VideoProgressItemData = {
  contentType: "video"
  contentId: number
  playMode: "watch" | "listen"
  playbackRate?: number
  progress?: ProgressPlayable
}

export type AudiobookProgressItemData = {
  contentType: "book"
  contentId: number
  playbackRate?: number
  progress?: ProgressPlayable
}

export type AlbumProgressItemData = {
  contentType: "album"
  contentId: number
  progress?: ProgressPlayable
  playbackRate?: number
}

export type PodcastEpisodeProgressItemData = {
  contentType: "podcast"
  contentId: number
  podcastEpisode?: number
  podcastEpisodeDuration?: number
  playbackRate?: number
  progress?: ProgressPlayable
  // DEPRECATED: This is legacy for podcasts. Now content and progress are
  // separate, but progress still has to store podcastEpisodeDuration
  // somehow since we don't calculate that server side. Previously this
  // wasn't stored separately from tracks, so for the sake of old progress
  // item data, we need to allow tracks to be passed when creating progress
  // items for podcasts, (so we can get the episode duration out if it's set
  // on the track).
  tracks?: {
    title: string
    artwork: string
    duration?: number
    artist?: string
    album?: string
  }[]
}

export type PlayableProgressItemData =
  | AudiobookProgressItemData
  | VideoProgressItemData
  | PodcastEpisodeProgressItemData
  | AlbumProgressItemData

/**
 * Functionality that progress item for "playable" content types (audio or
 * video) must implement.
 */
export class PlayableProgressItem<
  Data extends PlayableProgressItemData = PlayableProgressItemData,
> extends BaseProgressItem<Data, ProgressPlayable> {
  /**
   * Searches through a list of tracks for one that matches the content and
   * position represented by this progress item.
   */
  getTrack = (tracks: Track[]) => {
    const progress = this.getProgress()
    const key = this.getKey()
    return tracks.find(
      (track) =>
        getProgressItemKeyFromTrack(track) === key &&
        (track.extra.contentType !== "book" ||
          track.extra.chapterIndex === progress.index) &&
        (track.extra.contentType !== "album" ||
          track.extra.trackIndex === progress.index),
    )
  }

  /**
   * Returns the total duration in milliseconds of the content represented by
   * the progress item. Returns undefined if the total duration is not available.
   */
  getContentDuration = (tracks: Track[]): number | undefined => {
    const data = this.getData()
    if (data.contentType === "podcast") {
      return data.podcastEpisodeDuration
    }

    const track = this.getTrack(tracks)

    if (track?.extra.contentType === "book") {
      return track.extra.audiobookDuration
    }

    if (track?.extra.contentType === "album") {
      return track.extra.albumDuration
    }

    return track?.duration
  }

  /**
   * Gets current position in milliseconds of the progress item within the
   * content represented by the progress item. Returns undefined if the position
   * is not available.
   */
  getContentPosition = (tracks: Track[]): number | undefined => {
    const progress = this.getProgress()
    const track = this.getTrack(tracks)

    if (!track) {
      return undefined
    }

    if (track.extra.contentType === "book") {
      return track.extra.chapterPositionOffset + progress.position
    }

    if (track.extra.contentType === "album") {
      return track.extra.trackPositionOffset + progress.position
    }

    return progress.position
  }

  hasPlaybackRate = () => Boolean(this.getData().playbackRate)

  getPlaybackRate = () => this.getData().playbackRate || 1

  getProgress = (): ProgressPlayable => {
    const { progress } = this.getData()
    return {
      position: progress?.position || 0,
      index: progress?.index || 0,
      finished: progress?.finished || false,
    }
  }

  inProgress = () => {
    const progress = this.getProgress()
    return !progress.finished && (progress.index > 0 || progress.position > 0)
  }
}

export class VideoProgressItem extends PlayableProgressItem<VideoProgressItemData> {
  getPlayMode = (): NonNullable<VideoProgressItemData["playMode"]> =>
    this.getData().playMode || "watch"
}

export class AudiobookProgressItem extends PlayableProgressItem<AudiobookProgressItemData> {}

export class AlbumProgressItem extends PlayableProgressItem<AlbumProgressItemData> {}

export class PodcastEpisodeProgressItem extends PlayableProgressItem<PodcastEpisodeProgressItemData> {
  getPodcastEpisodeNumber = (): number => this.getData().podcastEpisode || 1

  getPodcastEpisodeDuration = (): number | undefined =>
    this.getData().podcastEpisodeDuration
}
